import { useEffect } from 'react';
import { Icon, type IconName } from './Icon';
import { Sheet } from '../shared/Sheet';
import { useT } from '../../i18n';
import { useBabyEvents, useMarkEventsSeen } from '../../data/queries';
import type { BabyEventRow, BabyEventType } from '../../data/dto';
import type { Baby } from '../../data/types';

interface Props {
  open: boolean;
  baby: Baby;
  onClose: () => void;
  /** Open the detail view related to an event (milestone, activity, …). */
  onOpenEvent?: (event: BabyEventRow) => void;
}

const EVENT_ICON: Record<BabyEventType, IconName> = {
  photo_added: 'photos',
  milestone_achieved: 'trophy',
  growth_added: 'growth',
  comment_added: 'mail',
  activity_completed: 'activities',
  questionnaire_answered: 'edit',
  member_joined: 'profile',
};

function relativeTime(utc: string, t: ReturnType<typeof useT>): string {
  const ms = Date.parse(utc.replace(' ', 'T') + 'Z');
  if (Number.isNaN(ms)) return '';
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('engagement.justNow', 'just now');
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(ms).toLocaleDateString();
}

function eventLine(e: BabyEventRow, babyName: string, t: ReturnType<typeof useT>): string {
  const actor = e.actor_name ?? t('notifications.someone', 'Someone');
  const p = e.payload ?? {};
  const count = typeof p.count === 'number' ? p.count : 1;
  const title = typeof p.title === 'string' && p.title !== '' ? p.title : null;

  const sub = (key: string, fallback: string) =>
    t(key, fallback)
      .replace('{actor}', actor)
      .replace('{baby}', babyName)
      .replace('{count}', String(count))
      .replace('{title}', title ?? '');

  switch (e.event_type) {
    case 'photo_added':
      return count > 1
        ? sub('notifications.photosAdded', '{actor} added {count} new photos of {baby}')
        : sub('notifications.photoAdded', '{actor} added a new photo of {baby}');
    case 'milestone_achieved':
      if (count > 1) return sub('notifications.milestonesAchieved', '{actor} marked {count} milestones as achieved');
      return title
        ? sub('notifications.milestoneAchievedTitled', '{actor} marked “{title}” as achieved')
        : sub('notifications.milestoneAchieved', '{actor} marked a milestone as achieved');
    case 'growth_added':
      return sub('notifications.growthAdded', '{actor} added a growth measurement for {baby}');
    case 'comment_added':
      return sub('notifications.commentAdded', '{actor} left a comment');
    case 'activity_completed':
      return title
        ? sub('notifications.activityCompletedTitled', '{actor} completed “{title}” with {baby}')
        : sub('notifications.activityCompleted', '{actor} completed an activity with {baby}');
    case 'questionnaire_answered':
      return sub('notifications.questionnaireAnswered', '{actor} answered a questionnaire about {baby}');
    case 'member_joined':
      return sub('notifications.memberJoined', '{actor} joined {baby}’s care team');
    default:
      return sub('notifications.genericEvent', 'New activity for {baby}');
  }
}

/**
 * Notification center sheet: the baby's recent activity feed (what every
 * member did), newest first. Opening it advances the read-cursor so the bell
 * badge clears.
 */
export function NotificationsSheet({ open, baby, onClose, onOpenEvent }: Props) {
  const t = useT();
  const eventsQ = useBabyEvents(open && baby.id ? baby.id : null);
  const markSeen = useMarkEventsSeen();

  // Clear the unseen badge once the feed is actually shown.
  useEffect(() => {
    if (open && baby.id && eventsQ.data && eventsQ.data.unseen_count > 0) {
      markSeen.mutate(baby.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, baby.id, eventsQ.data?.unseen_count]);

  const events = eventsQ.data?.events ?? [];
  const seenAtMs = eventsQ.data?.seen_at ? Date.parse(eventsQ.data.seen_at.replace(' ', 'T') + 'Z') : 0;

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="86dvh" label={t('notifications.title', 'Notifications')}>
      <div className="flex flex-col overflow-y-auto px-6 pb-8 pt-0">
        <div className="flex items-center justify-between pb-4">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('notifications.title', 'Notifications')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        <h2 className="mb-4 font-serif text-2xl tracking-tight text-ink">
          {t('notifications.latestFor', 'Latest for {name}').replace('{name}', baby.name)}
        </h2>

        {eventsQ.isLoading && (
          <p className="py-8 text-center text-sm text-ink-3">{t('common.loading', 'Loading…')}</p>
        )}

        {!eventsQ.isLoading && events.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <Icon name="bell" size={28} color="var(--color-ink-3)" />
            <p className="max-w-xs text-sm leading-relaxed text-ink-2">
              {t('notifications.empty', 'Nothing here yet. Photos, milestones and other updates from everyone caring for {name} will show up here.').replace('{name}', baby.name)}
            </p>
          </div>
        )}

        <ul className="flex flex-col">
          {events.map((e) => {
            const ms = Date.parse(e.created_at.replace(' ', 'T') + 'Z');
            const isNew = seenAtMs > 0 ? ms > seenAtMs : true;
            return (
              <li key={e.id} className="border-b border-hairline last:border-b-0">
                <button
                  onClick={() => onOpenEvent?.(e)}
                  className="flex w-full items-start gap-3 py-3.5 text-left transition-colors hover:bg-grey-soft/40 active:bg-grey-soft/60"
                >
                  <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-grey-soft">
                    <Icon name={EVENT_ICON[e.event_type] ?? 'bell'} size={17} color="var(--color-ink-2)" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] leading-snug text-ink">
                      {eventLine(e, baby.name, t)}
                    </span>
                    <span className="mt-0.5 block text-[11.5px] text-ink-3">{relativeTime(e.created_at, t)}</span>
                  </span>
                  {isNew && <span className="mt-2 size-2 shrink-0 rounded-full bg-accent" aria-hidden />}
                  <span className="mt-1.5 shrink-0">
                    <Icon name="chevron" size={15} color="var(--color-ink-3)" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Sheet>
  );
}

/** Bell button with unseen-count badge, for screen headers / the sidebar. */
export function NotificationBell({ babyId, onOpen }: { babyId: string | null; onOpen: () => void }) {
  const t = useT();
  const eventsQ = useBabyEvents(babyId);
  const unseen = eventsQ.data?.unseen_count ?? 0;

  return (
    <button
      onClick={onOpen}
      aria-label={t('notifications.title', 'Notifications')}
      className="relative flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-grey-soft"
    >
      <Icon name="bell" size={22} color="var(--color-ink)" />
      {unseen > 0 && (
        <span
          className="absolute right-1 top-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white"
          aria-label={`${unseen} unseen`}
        >
          {unseen > 9 ? '9+' : unseen}
        </span>
      )}
    </button>
  );
}
